"use client";

/**
 * OpenStore Orion Design System - Ant Design Wrapper
 * Main entry point for the themed component library
 */

// Export the main provider
export { OrionProvider } from "./OrionProvider";

// Export theme configuration
export {
  orionTheme,
  orionColors,
  orionTypography,
  orionSpacing,
} from "./theme/orion-theme";

// Export wrapped components
export {
  Button,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  GhostButton,
} from "./components/Button";
export { Card, CardMeta } from "./components/Card";
// Table components are exported below
export {
  Select,
  SelectOption,
  SelectOptGroup,
  SearchableSelect,
  MultiSelect,
  TagSelect,
  AsyncSelect,
} from "./components/Select";
export {
  Dropdown,
  ContextMenuDropdown,
  HoverDropdown,
  ClickDropdown,
  ActionDropdown,
  SelectDropdown,
} from "./components/Dropdown";
export {
  FloatButton,
  FloatButtonGroup,
  FloatButtonBackTop,
  AddFloatButton,
  BackToTopButton,
} from "./components/FloatButton";
export {
  StatusPill,
  SuccessPill,
  WarningPill,
  ErrorPill,
  InfoPill,
  ActivePill,
  DraftPill,
  DeletePill,
} from "./components/StatusPill";
export {
  Tag,
  SuccessTag,
  WarningTag,
  ErrorTag,
  InfoTag,
  ActiveTag,
  DraftTag,
  DeleteTag,
} from "./components/Tag";
export {
  DatePicker,
  DateRangePicker,
  TimePicker,
  DateTimePicker,
  RangePicker,
  WeekPicker,
  MonthPicker,
  QuarterPicker,
  YearPicker,
} from "./components/DatePicker";
export {
  Pagination,
  SimplePagination,
  CompactPagination,
  MinimalPagination,
} from "./components/Pagination";
export {
  Tabs,
  PillTabs,
  SegmentedTabs,
  ControlTabs,
  TabPane,
} from "./components/Tabs";
export { Checkbox, CheckboxGroup } from "./components/Checkbox";
export { Switch } from "./components/Switch";
export { Radio, RadioGroup, RadioButton } from "./components/Radio";
export { MoreInfoButton } from "./components/MoreInfoButton";
export {
  ActionButton,
  ArchiveButton,
  DeleteButton,
  CancelButton,
} from "./components/ActionButton";
export { ActionMenu } from "./components/ActionMenu";
export { Thumbnail } from "./components/Thumbnail";
export {
  Modal,
  ConfirmModal,
  InfoModal,
  WarningModal,
} from "./components/Modal";

// Table Components
export {
  Table,
  ProductTable,
  SimpleTable,
  CompactTable,
} from "./components/Table";

// Re-export commonly used Ant Design components with types
export {
  Input,
  Form,
  Drawer,
  Menu,
  Badge,
  Tooltip,
  Popover,
  Space,
  Divider,
  Typography,
  Layout,
  Row,
  Col,
  Upload,
  Progress,
  Spin,
  Alert,
  message,
  notification,
  Breadcrumb,
  Steps,
  Anchor,
  BackTop,
  Avatar,
  List,
  Skeleton,
  Empty,
  Result,
  Statistic,
  Tree,
  Transfer,
  Cascader,
  AutoComplete,
  Rate,
  Slider,
  ColorPicker,
  QRCode,
  Watermark,
  Tour,
  Segmented,
  Image,
  Calendar,
  Carousel,
  Collapse,
  Descriptions,
  Timeline,
  Affix,
} from "antd";

// Re-export Ant Design types for convenience
export type {
  ButtonProps as AntButtonProps,
  CardProps as AntCardProps,
  InputProps,
  SelectProps,
  FormProps,
  TableProps,
  ModalProps,
  DrawerProps,
  MenuProps,
  TabsProps,
  BadgeProps,
  TagProps,
  TooltipProps,
  PopoverProps,
  DropdownProps,
  SpaceProps,
  DividerProps,
  TypographyProps,
  LayoutProps,
  RowProps,
  ColProps,
  CheckboxProps,
  RadioProps,
  SwitchProps,
  DatePickerProps,
  TimePickerProps,
  UploadProps,
  ProgressProps,
  SpinProps,
  AlertProps,
  BreadcrumbProps,
  PaginationProps,
  StepsProps,
  AnchorProps,
  AvatarProps,
  ListProps,
  SkeletonProps,
  EmptyProps,
  ResultProps,
  StatisticProps,
  TreeProps,
  TransferProps,
  CascaderProps,
  AutoCompleteProps,
  RateProps,
} from "antd";

// Export Ant Design icons for convenience
export * from "@ant-design/icons";

// Version and metadata
export const version = "0.2.0";

export const designSystem = {
  name: "OpenStore Orion",
  version,
  description: "OpenStore admin component library built on Ant Design",
  repository: "https://github.com/primathontech/os-apps-orion",
  basedOn: "Ant Design 5.x",
} as const;
